import { useState, useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { auth } from '@/config/firebase';
import { useAuthStore } from '@/store/authStore';
import { useTenantStore } from '@/store/tenantStore';
import { useLocalizationStore } from '@/store/localizationStore';
import {
  getJobDetails,
  getSimilarJobs,
  getAppliedSavedJobs,
  getSavedJobsCount,
  saveJob,
  unsaveJob,
} from '@/services/api/homeApi';
import { getUserDetails } from '@/services/api/authApi';
import { analyticsService } from '@/services/analytics/analyticsService';
import { readAppliedSavedCache, withAppliedSaved } from '@/features/home/utils/appliedSavedCache';
import { getRelevanceData } from '@/features/jobs/utils/relevanceData';
import type { JobEntity } from '@/types/job.types';
import type { CandidateDetails } from '@/services/api/authApi';
import { ROUTES } from '@/app/router/routes';
import type { LoginReturnInput } from '@/services/auth/postLoginRedirect';

// Toast texts — Flutter ErrorConstants (Toast translates them)
const SAVE_JOB_SUCCESS = 'Saved the job successfully';
const SAVE_JOB_FAILED = 'Failed to save the job!';
const UNSAVE_JOB_SUCCESS = 'Unsaved the job successfully';
const UNSAVE_JOB_FAILED = 'Failed to unsave the job!';

/** Flutter JobDetailsViewBloc analytics location */
const JOB_DETAILS_VIEW = 'Job Details View';

/** Tenant preference jobDetailsPageConfig.additionalSections item */
export interface JobDetailsAdditionalSection {
  label?: string;
  description?: string;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export interface JobDetailsState {
  jobDetails: JobEntity | null;
  similarJobs: JobEntity[];
  isLoading: boolean;
  isJobDetailsFailed: boolean;
  isSimilarJobsFailed: boolean;
  similarJobsLoading: boolean;
  jobClosedOrInactive: boolean;
  jobNotFound: boolean;
  requiredSkills: string[];
  otherSkills: string[];
  /** Relevance stored by the list card that opened this job (signed-in users only) */
  relevance: Record<string, unknown> | null;
  jobDetailsAdditionalSection: JobDetailsAdditionalSection[];
  toast: { message: string; type: 'success' | 'error' } | null;
}

export interface JobDetailsActions {
  onSaveJob: (entity: JobEntity | null, onSimilarJobs: boolean, index?: number) => void;
  onUnsaveJob: (entity: JobEntity | null, index?: number) => void;
  clearToast: () => void;
  dismissJobClosed: () => void;
}

/**
 * Job details logic — mirrors Flutter JobDetailsViewBloc. _Init (job details, similar jobs,
 * the saved-jobs count and the pending save after login) runs for each job id and again on
 * every language change (languageStream).
 */
export function useJobDetails(displayId: string): {
  state: JobDetailsState;
  actions: JobDetailsActions;
} {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const preferences = useTenantStore((s) => s.preferences);
  const currentLanguage = useLocalizationStore((s) => s.currentLanguage);
  // Set by login/MFA after an anonymous save (Flutter JobDetailsViewState.profileInput)
  const profileInput = (location.state as { profileInput?: LoginReturnInput } | null)?.profileInput;
  const saveJobInProgress = !!profileInput?.saveJobInProgress;

  const [jobDetails, setJobDetails] = useState<JobEntity | null>(null);
  const [similarJobs, setSimilarJobs] = useState<JobEntity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isJobDetailsFailed, setIsJobDetailsFailed] = useState(false);
  const [isSimilarJobsFailed, setIsSimilarJobsFailed] = useState(false);
  const [similarJobsLoading, setSimilarJobsLoading] = useState(true);
  const [jobClosedOrInactive, setJobClosedOrInactive] = useState(false);
  const [jobNotFound, setJobNotFound] = useState(false);
  const [requiredSkills, setRequiredSkills] = useState<string[]>([]);
  const [otherSkills, setOtherSkills] = useState<string[]>([]);
  const [relevance, setRelevance] = useState<Record<string, unknown> | null>(null);
  const [jobDetailsAdditionalSection, setJobDetailsAdditionalSection] = useState<
    JobDetailsAdditionalSection[]
  >([]);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);

  const candidateDetailsRef = useRef<CandidateDetails | null>(null);
  const jobDetailsRef = useRef<JobEntity | null>(null);
  jobDetailsRef.current = jobDetails;
  /** Ignores responses for a job id / language that has since changed */
  const loadSeq = useRef(0);
  const saveAfterLoginDone = useRef(false);
  const preferencesRef = useRef(preferences);
  preferencesRef.current = preferences;

  const refreshSavedJobsCount = useCallback(() => {
    // Updates the header badge (Flutter AppStateNotifier.changeJobCount)
    getSavedJobsCount().catch(() => {});
  }, []);

  // ── Flutter _GetJobDetails ──
  const fetchJobDetails = useCallback(async (id: string, seq: number) => {
    setIsLoading(true);
    setIsJobDetailsFailed(false);
    setRelevance(null);
    try {
      const data = await getJobDetails(id);
      if (seq !== loadSeq.current) return;

      const reqSkills: string[] = [];
      const otherSkillsList: string[] = [];
      for (const skill of data.skills ?? []) {
        const name = skill.skill ?? '';
        if (skill.isMandatory) reqSkills.push(name);
        else otherSkillsList.push(name);
      }
      setRequiredSkills(reqSkills);
      setOtherSkills(otherSkillsList);

      // Flutter getAdditionalSectionForJobDetails()
      const additional = (
        preferencesRef.current?.jobDetailsPageConfig as Record<string, unknown> | undefined
      )?.additionalSections;
      setJobDetailsAdditionalSection(
        Array.isArray(additional) ? (additional as JobDetailsAdditionalSection[]) : [],
      );

      // Cached applied / saved status first (LocalDataService.getAppliedSavedJobs)
      const [withCache] = withAppliedSaved([data], readAppliedSavedCache());
      setJobDetails(withCache);
      setIsLoading(false);

      // Relevance from the list card that opened this job — signed-in users only
      if (auth.currentUser) {
        setRelevance(getRelevanceData(data.displayId));
      }
    } catch {
      if (seq !== loadSeq.current) return;
      setIsJobDetailsFailed(true);
      setJobClosedOrInactive(true);
      setJobNotFound(true);
      setIsLoading(false);
    }
  }, []);

  // ── Flutter _GetSimilarJobs ──
  const fetchSimilarJobs = useCallback(async (id: string, seq: number) => {
    setSimilarJobsLoading(true);
    setIsSimilarJobsFailed(false);
    try {
      const data = await getSimilarJobs(id);
      if (seq !== loadSeq.current) return;
      setSimilarJobs(withAppliedSaved(data ?? [], readAppliedSavedCache()));
      setSimilarJobsLoading(false);
    } catch {
      if (seq !== loadSeq.current) return;
      setIsSimilarJobsFailed(true);
      setSimilarJobsLoading(false);
    }
  }, []);

  // ── Flutter _GetSavedAppliedJobs — details and similar jobs ──
  const refreshAppliedSaved = useCallback(async (seq: number) => {
    try {
      const data = await getAppliedSavedJobs();
      if (seq !== loadSeq.current) return;
      setJobDetails((prev) => (prev ? withAppliedSaved([prev], data)[0] : prev));
      setSimilarJobs((prev) => withAppliedSaved(prev, data));
    } catch {
      // Non-critical
    }
  }, []);

  // ── Complete save-after-login — Flutter JobDetailsViewBloc._OnClickSaveJobAfterLogin ──
  const completeSaveAfterLogin = useCallback(async () => {
    const saveJobJson = localStorage.getItem('saveJobDetails');
    // Nothing pending (e.g. a refresh, which keeps history state) — nothing to report
    if (!saveJobJson || !auth.currentUser) return;
    // One attempt per login: a pending save left behind (failed or abandoned) must not replay
    // on a later visit or a refresh of this page
    localStorage.removeItem('saveJobDetails');

    try {
      const decoded = JSON.parse(saveJobJson) as Record<string, unknown>;
      if (!decoded.requisitionId) throw new Error('No pending save');

      const details = candidateDetailsRef.current ?? (await getUserDetails());
      candidateDetailsRef.current = details;
      await saveJob({
        candidateId: (details as Record<string, unknown>).id,
        requisitionId: decoded.requisitionId,
        candidateDisplayId: (details as Record<string, unknown>).displayId,
        requisitionDisplayId: decoded.requisitionDisplayId,
        primaryEmailId: details.primaryEmailId,
        exprncFromMnthInYears: decoded.exprncFromMnthInYears,
        exprncToMnthInYears: decoded.exprncToMnthInYears,
      });

      // Mark the saved job — the main job or one of the similar jobs. Matched by displayId
      // inside the updaters: this can finish before job details have loaded.
      setJobDetails((prev) =>
        prev?.displayId === decoded.requisitionDisplayId ? { ...prev, isSavedJob: true } : prev,
      );
      setSimilarJobs((prev) =>
        prev.map((j) =>
          j.displayId === decoded.requisitionDisplayId ? { ...j, isSavedJob: true } : j,
        ),
      );
      setToast({ message: SAVE_JOB_SUCCESS, type: 'success' });
      // Flutter logs job_unsave here, with the details job (kept as is)
      void analyticsService.logJobUnsave({
        location: JOB_DETAILS_VIEW,
        jobId: jobDetailsRef.current?.id ?? '',
        jobTitle: jobDetailsRef.current?.jobTitle ?? '',
      });
      refreshSavedJobsCount();
    } catch {
      setToast({ message: SAVE_JOB_FAILED, type: 'error' });
    }
  }, [refreshSavedJobsCount]);

  // ── Init — per job id and on every language change (Flutter _Init / languageStream) ──
  useEffect(() => {
    if (!displayId) return;
    const seq = ++loadSeq.current;

    void fetchJobDetails(displayId, seq);
    void fetchSimilarJobs(displayId, seq);

    if (auth.currentUser) {
      if (!saveJobInProgress) refreshSavedJobsCount();
      getUserDetails()
        .then((d) => {
          candidateDetailsRef.current = d;
          if (saveJobInProgress && !saveAfterLoginDone.current) {
            saveAfterLoginDone.current = true;
            void completeSaveAfterLogin();
          }
        })
        .catch(() => {});
      void refreshAppliedSaved(seq);
    }
  }, [displayId, currentLanguage]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Re-fetch applied/saved when the user signs in or out on this page ──
  const userRef = useRef(user);
  useEffect(() => {
    if (userRef.current === user) return;
    userRef.current = user;
    if (user) void refreshAppliedSaved(loadSeq.current);
  }, [user, refreshAppliedSaved]);

  // ── Actions ──

  const setSimilarSaveLoading = useCallback((index: number, isLoadingForSaveJobButton: boolean) => {
    setSimilarJobs((prev) => {
      if (!prev[index]) return prev;
      const copy = [...prev];
      copy[index] = { ...copy[index], isLoadingForSaveJobButton };
      return copy;
    });
  }, []);

  // Flutter _OnClickSaveJob — logs the save whether or not it worked
  const onSaveJob = useCallback(
    async (entity: JobEntity | null, onSimilarJobs: boolean, index?: number) => {
      if (!entity) return;

      if (!auth.currentUser) {
        // Anonymous save — remember the job and log in first
        localStorage.setItem(
          'saveJobDetails',
          JSON.stringify({
            requisitionId: entity.id,
            requisitionDisplayId: entity.displayId,
            exprncFromMnthInYears: entity.requiredExperienceInMonths?.from,
            exprncToMnthInYears: entity.requiredExperienceInMonths?.to,
          }),
        );
        const loginInput: LoginReturnInput = {
          saveJobInProgress: true,
          onSimiliarJobs: onSimilarJobs,
          previousPage: ROUTES.JOBS,
          jobDisplayId: jobDetailsRef.current?.displayId,
        };
        void analyticsService.logScreenView({ screenName: ROUTES.LOGIN });
        navigate(ROUTES.LOGIN, { state: loginInput });
        return;
      }

      if (index !== undefined) setSimilarSaveLoading(index, true);
      try {
        const details = candidateDetailsRef.current ?? (await getUserDetails());
        candidateDetailsRef.current = details;
        await saveJob({
          candidateId: (details as Record<string, unknown>).id,
          requisitionId: entity.id,
          candidateDisplayId: (details as Record<string, unknown>).displayId,
          requisitionDisplayId: entity.displayId,
          primaryEmailId: details.primaryEmailId,
          exprncFromMnthInYears: entity.requiredExperienceInMonths?.from,
          exprncToMnthInYears: entity.requiredExperienceInMonths?.to,
        });

        if (index !== undefined) {
          setSimilarJobs((prev) => {
            if (!prev[index]) return prev;
            const copy = [...prev];
            copy[index] = { ...copy[index], isSavedJob: true, isLoadingForSaveJobButton: false };
            return copy;
          });
        } else {
          setJobDetails((prev) => (prev ? { ...prev, isSavedJob: true } : prev));
        }
        setToast({ message: SAVE_JOB_SUCCESS, type: 'success' });
      } catch {
        if (index !== undefined) setSimilarSaveLoading(index, false);
        setToast({ message: SAVE_JOB_FAILED, type: 'error' });
      }
      refreshSavedJobsCount();
      void analyticsService.logJobSave({
        location: JOB_DETAILS_VIEW,
        jobId: entity.id ?? '',
        jobTitle: entity.jobTitle ?? '',
      });
    },
    [navigate, setSimilarSaveLoading, refreshSavedJobsCount],
  );

  // Flutter _OnClickUnsaveJob — logs job_save (sic) whether or not it worked
  const onUnsaveJob = useCallback(
    async (entity: JobEntity | null, index?: number) => {
      if (!entity) return;
      if (index !== undefined) setSimilarSaveLoading(index, true);

      try {
        if (!(await unsaveJob(entity.displayId ?? ''))) throw new Error('Unsave failed');
        if (index !== undefined) {
          setSimilarJobs((prev) => {
            if (!prev[index]) return prev;
            const copy = [...prev];
            copy[index] = { ...copy[index], isSavedJob: false, isLoadingForSaveJobButton: false };
            return copy;
          });
        } else {
          setJobDetails((prev) => (prev ? { ...prev, isSavedJob: false } : prev));
        }
        setToast({ message: UNSAVE_JOB_SUCCESS, type: 'success' });
      } catch {
        if (index !== undefined) setSimilarSaveLoading(index, false);
        setToast({ message: UNSAVE_JOB_FAILED, type: 'error' });
      }
      void analyticsService.logJobSave({
        location: JOB_DETAILS_VIEW,
        jobId: entity.id ?? '',
        jobTitle: entity.jobTitle ?? '',
      });
      refreshSavedJobsCount();
    },
    [setSimilarSaveLoading, refreshSavedJobsCount],
  );

  const clearToast = useCallback(() => setToast(null), []);

  // Flutter warning dialog: navigateTo(homeRoute, isClearStack: true)
  const dismissJobClosed = useCallback(() => {
    setJobClosedOrInactive(false);
    void analyticsService.logScreenView({ screenName: ROUTES.HOME });
    navigate(ROUTES.HOME, { replace: true });
  }, [navigate]);

  return {
    state: {
      jobDetails,
      similarJobs,
      isLoading,
      isJobDetailsFailed,
      isSimilarJobsFailed,
      similarJobsLoading,
      jobClosedOrInactive,
      jobNotFound,
      requiredSkills,
      otherSkills,
      relevance,
      jobDetailsAdditionalSection,
      toast,
    },
    actions: {
      onSaveJob,
      onUnsaveJob,
      clearToast,
      dismissJobClosed,
    },
  };
}
